import { ServicoGeralResponse } from '@/core/api/sicom-api/servicos/shared/servico-geral.model';
import { ServicoGeralService } from '@/core/api/sicom-api/servicos/shared/servico-geral.service';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { MenuService } from '@/layout/service/menu.service';
import { TipoServicoPessoa } from '@/features/servicos/shared/enums/servico-disponivel.enum';
import { CatalogoServicosStore, type ServicoDoModulo } from '@/features/servicos/shared/catalogo-servicos.store';
import { UfEfetivaStore } from '@/features/servicos/shared/uf-efetiva.store';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggerService } from '@/core/services/logger.service';
import { SERVICOS_ROUTES } from '@/features/servicos/servicos.constants';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { Subscription, combineLatest } from 'rxjs';
import { PessoaService } from '@/shared/services/pessoa.service';
import { ToastModule } from 'primeng/toast';

/** Shell do módulo de Serviços: monta o menu lateral conforme o tipo (PF/PJ) e abre o diálogo de pesquisa de pessoa. */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-servicos-content',
    standalone: true,
    imports: [RouterModule, Pesquisa, ToastModule],
    template: `
        <p-toast></p-toast>
        <main class="w-full">
            @if (erroCatalogo) {
                <p class="servicos-erro-catalogo" role="alert">{{ erroCatalogo }}</p>
            }
            @if (precisaEscolherUf()) {
                <p class="servicos-escolha-uf" role="status">Selecione uma UF para ver os protocolos.</p>
            }
            <div>
                <router-outlet></router-outlet>
            </div>
        </main>
        <app-pesquisa [showDialog]="pesquisarDialog()" (showDialogChange)="pesquisarDialog.set($event)" (pessoa)="carregarPessoa($event)"></app-pesquisa>
    `,
    // Componente com template inline e sem `styleUrls`: o menor acréscimo coerente é o `styles` inline
    // do próprio `@Component`, em vez de criar um `.scss` novo só para uma faixa. Cores/borda seguem o
    // mesmo vocabulário de "erro" já usado no módulo (`.detail-situacao.cancelado`,
    // `.detail-badge-pagamento.nao-pago` em consulta-protocolo-servicos.scss): #991b1b sobre
    // #ef444466/#ef44441a.
    styles: `
        /*
            "Ainda não escolheu" não é erro: é instrução. Por isso vocabulário neutro (borda e texto
            secundários), e não o vermelho da faixa de erro do catálogo logo acima.
        */
        .servicos-escolha-uf {
            margin: 0 0 1rem;
            padding: 0.75rem 1rem;
            border: 1px dashed var(--surface-border, #e5e7eb);
            border-radius: 0.5rem;
            color: var(--text-color-secondary, #6b7280);
            font-size: 0.9rem;
        }

        .servicos-erro-catalogo {
            margin: 0 0 1rem;
            padding: 0.75rem 1rem;
            border: 1px solid #ef444466;
            border-radius: 0.5rem;
            background: #ef44441a;
            color: #991b1b;
            font-weight: 600;
            font-size: 0.9rem;
        }
    `
})
export class ServicosContent implements OnInit {
    private readonly menuService = inject(MenuService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly servicoGeralService = inject(ServicoGeralService);
    private readonly pessoaService = inject(PessoaService);
    private readonly logger = inject(LoggerService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly catalogo = inject(CatalogoServicosStore);
    private readonly ufEfetivaStore = inject(UfEfetivaStore);

    readonly pesquisarDialog = signal(false);
    readonly rootPathBase = SERVICOS_ROUTES.ROOT;
    readonly tipo = signal<TipoServicoPessoa>('pessoa-fisica');

    /** "Ainda não escolhido" é diferente de "lista vazia": a tela precisa dizer coisas diferentes. */
    readonly precisaEscolherUf = computed(() => this.ufEfetivaStore.precisaEscolherUf());

    /** As contagens do portal viraram signal para o menu poder ser remontado quando o catálogo chegar. */
    private readonly contagens = signal<ServicoGeralResponse[]>([]);

    private servicosGeraisSub?: Subscription;
    private menuRequestId = 0;

    /**
     * O erro do catálogo aparece como faixa aqui, no shell do módulo, e NÃO como toast: um toast some
     * sozinho, e a falha em montar o menu do módulo não é o tipo de coisa que se resolve reclicando —
     * a faixa persiste enquanto o catálogo não carregar, e vale para toda rota filha.
     */
    get erroCatalogo(): string | null {
        return this.catalogo.erro();
    }

    constructor() {
        // Duas fontes assíncronas alimentam o mesmo menu (as contagens do portal e o catálogo). Um
        // effect é o que garante que a segunda a chegar também redesenhe — antes, o menu era montado
        // dentro do subscribe das contagens e o catálogo chegaria tarde demais.
        effect(() => {
            this.menuService.setMenu(this.buildMenu(this.tipo(), this.contagens(), this.catalogo.servicos()));
        });
    }

    ngOnInit(): void {
        // combineLatest, e não dois subscribes separados: as contagens do menu (`carregarContagens`)
        // dependem da UF do store, e o `?uf` muda SEM o `:tipo` mudar junto — dois subscribes deixaria
        // a troca de UF sem recarregar nada, que era exatamente o defeito (o menu nunca refletia a UF
        // escolhida). `definirUfEscolhida` vem ANTES de ler `ufEfetiva()` dentro do mesmo emit, então a
        // contagem nunca lê o valor da UF anterior.
        combineLatest([this.route.paramMap, this.route.queryParamMap])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([params, queryParams]) => {
                this.ufEfetivaStore.definirUfEscolhida(queryParams.get('uf'));

                const tipoParam = params.get('tipo');
                const tipo: TipoServicoPessoa = tipoParam === 'pessoa-juridica' ? 'pessoa-juridica' : 'pessoa-fisica';
                // Só recarrega o catálogo quando o SETOR de fato muda — combinar com o `?uf` faria uma
                // troca de UF recarregar o catálogo à toa, uma requisição que nada tem a ver com UF.
                if (tipo !== this.tipo()) {
                    this.tipo.set(tipo);
                    this.catalogo.carregarPara(tipo);
                }

                this.carregarContagens();
            });
    }

    private carregarContagens(): void {
        // Store, e não uma derivação local: `obterUfCrm()` devolvia 'BR' pro nacional (o servidor
        // recusaria) e nunca era reexecutada quando só o `?uf` mudava — os badges do menu "Serviços"
        // nunca refletiam a UF escolhida.
        const ufCrm = this.ufEfetivaStore.ufEfetiva();
        const requestId = ++this.menuRequestId;

        this.servicosGeraisSub?.unsubscribe();
        this.contagens.set([]);

        // Sem UF efetiva (nacional que abriu o módulo e ainda não escolheu), NÃO se pergunta.
        // Aqui havia um `?? ''` justificado por "o servidor devolve vazio": é falso. O
        // `ServicoPortalConsultaService` recusa `uf_crm` em branco com 400 "O parametro uf_crm e
        // obrigatorio", e o interceptor global joga isso num toast — que este módulo passou a ter.
        // Resultado: erro vermelho na cara do nacional a cada entrada no módulo, antes de ele ter
        // qualquer chance de escolher. Menu sem contagem é o estado correto de "ainda não perguntei".
        if (!ufCrm) {
            return;
        }

        this.servicosGeraisSub = this.servicoGeralService
            .consultar({ ufCrm })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (servicosGerais) => {
                    if (requestId !== this.menuRequestId) {
                        return;
                    }
                    this.contagens.set(servicosGerais ?? []);
                },
                error: () => {
                    if (requestId !== this.menuRequestId) {
                        return;
                    }
                    this.contagens.set([]);
                }
            });
    }

    private buildMenu(tipo: TipoServicoPessoa, servicosGerais: ServicoGeralResponse[], servicosDoModulo: ServicoDoModulo[]): AppMenuItem[] {
        const base = `/${this.rootPathBase}/${tipo}/${SERVICOS_ROUTES.ITEM}`;
        // Gate do módulo: sem a role de Serviços do tipo, os itens do menu ficam cinza (não clicáveis).
        const roleModulo = tipo === 'pessoa-juridica' ? PERMISSOES.pessoaJuridica.servicos.visualizar : PERMISSOES.pessoaFisica.servicos.visualizar;
        const quantidadesPorTipoServico = new Map<number, string>();

        servicosGerais.forEach((servico) => {
            if (servico?.tipoServico == null) {
                return;
            }
            quantidadesPorTipoServico.set(servico.tipoServico, servico.quantidade ?? '0');
        });

        const itensServicos = servicosDoModulo
            .filter((servico) => this.temMaisDeUmProtocolo(quantidadesPorTipoServico.get(servico.id)))
            .map(
                (servico): AppMenuItem => ({
                    label: servico.rotulo,
                    icon: 'pi pi-fw pi-bell',
                    badge: quantidadesPorTipoServico.get(servico.id) ?? '0',
                    routerLink: [base, servico.codigo],
                    queryParamsHandling: 'preserve',
                    fragment: servico.rotulo,
                    data: { breadcrumb: servico.rotulo },
                    permission: roleModulo
                })
            );

        return [
            {
                label: 'Home',
                items: [
                    {
                        label: 'Serviços',
                        expanded: true,
                        active: true,
                        items: itensServicos
                    },
                    {
                        label: 'Consultar Protocolo',
                        icon: 'pi pi-fw pi-search',
                        routerLink: [`/${this.rootPathBase}/${tipo}/${SERVICOS_ROUTES.PROTOCOLO}`],
                        queryParamsHandling: 'preserve',
                        data: { breadcrumb: 'Consultar Protocolo' },
                        permission: roleModulo
                    },
                    {
                        // O Envelhecimento não tem item próprio aqui: ele virou um dos relatórios, e
                        // se abre pela vitrine. Dois caminhos para a mesma tela é como uma delas fica
                        // para trás quando a rota mudar.
                        label: 'Relatórios',
                        icon: 'pi pi-fw pi-file-import',
                        routerLink: [`/${this.rootPathBase}/${tipo}/${SERVICOS_ROUTES.RELATORIOS}`],
                        queryParamsHandling: 'preserve',
                        data: { breadcrumb: 'Relatórios' },
                        permission: roleModulo
                    }
                ]
            }
        ];
    }

    private temMaisDeUmProtocolo(quantidade?: string): boolean {
        const quantidadeNumerica = Number.parseInt((quantidade ?? '0').trim(), 10);
        return Number.isFinite(quantidadeNumerica) && quantidadeNumerica >= 1;
    }

    private async executar(rota: string) {
        await this.router.navigateByUrl('/', { skipLocationChange: true });
        await this.router.navigate([rota]);
    }

    carregarPessoa(pessoa?: Pessoa) {
        if (pessoa) {
            this.pessoaService.setPessoa(pessoa);
            this.executar(`${this.rootPathBase}/${this.tipo()}`);
        } else {
            this.logger.error('Registro não encontrado!');
        }
    }
}
